import React from 'react';
import { SOCIAL_LINKS, trackSocialClick } from '../lib/socialLinks';

/**
 * "Follow us on socials" homepage tile — sits just above the footer.
 *
 * Bold, high-contrast card that gives each outlet a proper visual anchor
 * (large icon, brand-coloured hover state, handle underneath). Designed to
 * be one of the last things a reader sees, when they're most likely to
 * convert from casual visitor → follower on another platform.
 */
const FollowUsBanner = () => {
  return (
    <section
      className="relative py-20 bg-gradient-to-br from-[var(--stone)] via-slate-900 to-[var(--stone)] text-white overflow-hidden"
      data-testid="home-follow-banner"
    >
      {/* Subtle radial glow behind the icons for depth */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[var(--madder)]/40 blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/80 text-[11px] font-bold uppercase tracking-[0.18em] mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--madder)] animate-pulse" />
            Follow the story
          </div>
          <h2
            className="text-3xl md:text-5xl font-bold tracking-tight"
            data-testid="home-follow-banner-h2"
          >
            Stay connected with Paisley
          </h2>
          <p className="text-base md:text-lg text-white/60 mt-4 max-w-2xl mx-auto">
            News, events and community stories drop first on our socials. Pick your platform and never miss a thing.
          </p>
        </div>

        {/* Icon grid — 3 cols on mobile, 6 on desktop */}
        <div
          className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4"
          data-testid="home-follow-banner-grid"
        >
          {SOCIAL_LINKS.map(({ name, url, handle, Icon, brand }) => (
            <a
              key={name}
              href={url}
              target="_blank"
              rel="me noopener noreferrer"
              aria-label={`Follow Paisley.org.uk on ${name}`}
              data-testid={`home-follow-${name.toLowerCase()}`}
              onClick={() => trackSocialClick(name, 'home-banner')}
              className="group relative flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-white/30 hover:bg-white/[0.08] transition-all duration-300 hover:-translate-y-1"
              style={{ '--brand-colour': brand }}
            >
              {/* Icon disc — background swaps to the platform's official
                  brand colour on hover for that instant "I know this app"
                  micro-recognition. */}
              <div
                className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center bg-white/10 text-white transition-all duration-300 group-hover:scale-110"
                style={{
                  transition: 'background-color 300ms, transform 300ms',
                }}
              >
                <span
                  className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ backgroundColor: brand }}
                >
                  <Icon size={26} className="text-white" />
                </span>
                <span className="relative group-hover:opacity-0 transition-opacity duration-300">
                  <Icon size={26} />
                </span>
              </div>

              <div className="text-center">
                <p className="text-sm font-semibold text-white">{name}</p>
                <p className="text-[11px] text-white/50 mt-0.5 truncate max-w-[8rem]" title={handle}>
                  {handle}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FollowUsBanner;
